import type { Schemas } from '@seoai/api-types';
import { create } from 'zustand';

/**
 * Panel dili (SEOAI-65/76).
 *
 * Dil listesi KOPYALANMAZ — `/locales` ucundan gelir. İlk sürümde burada sabit
 * ['tr','en'] duruyordu ve bu, SEOAI-76'nın "config/i18n.php tek kaynak, yeni
 * dil kod değişikliği istemez" kuralını kırıyordu: backend'e eklenen bir dil
 * panele yansımıyordu (SEOAI-78 incelemesi).
 *
 * Tip de üretilen şemadan gelir; katalog alanları elle yazılmaz.
 */
export type LocaleCatalog = Schemas['LocaleCatalogDTO'];
export type LocaleEntry = LocaleCatalog['locales'][number];

const STORAGE_KEY = 'seoai.locale';

/**
 * Katalog gelene kadarki başlangıç dili. Bir değer ŞART: ilk istek (katalog
 * çağrısının kendisi dahil) X-Locale başlığıyla gider. Saklanmış değer varsa o,
 * yoksa tarayıcı dili, o da tutmazsa 'tr'.
 */
function initialLocale(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Erişilemez depolama akışı durdurmaz.
  }

  return navigator.language.split('-')[0] ?? 'tr';
}

type LocaleState = {
  locale: string;
  catalog: LocaleCatalog | null;
  setLocale: (locale: string) => void;
  /** Katalog geldiğinde: seçili dil katalogda yoksa katalogun varsayılanına düşülür. */
  applyCatalog: (catalog: LocaleCatalog) => void;
};

export const useLocale = create<LocaleState>((set, get) => ({
  locale: initialLocale(),
  catalog: null,

  setLocale: (locale) => {
    set({ locale });
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Yazamamak dil değişimini engellemez; sekme ömrü boyunca geçerli kalır.
    }
  },

  applyCatalog: (catalog) => {
    const known = catalog.locales.some((entry) => entry.code === get().locale);

    set({ catalog });

    /*
     * Saklanmış dil artık desteklenmiyorsa (pasife alınmış ya da kaldırılmış)
     * sessizce varsayılana düşülür — sunucu zaten öyle davranır (SEOAI-65).
     */
    if (!known) get().setLocale(catalog.default);
  },
}));

/** React ağacı dışından (API istemcisi) okuma. */
export const currentLocale = (): string => useLocale.getState().locale;
